// No longer need to import isLegacyMappingFormat from mappingFormatUtils

/**
 * Extracts column mappings from either old or new mapping format
 * @param {Object} inventory - The inventory object
 * @returns {Object} Column mappings in the old format for backward compatibility
 */
export const getColumnMappings = (inventory) => {
  // If inventory has mappingConfig (new format), convert it to old format
  if (inventory.mappingConfig) {
    return convertMappingConfigToColumnMappings(inventory.mappingConfig);
  }

  // If inventory has columnMappings (legacy format), return it directly
  if (inventory.columnMappings) {
    return inventory.columnMappings;
  }

  // Fallback: return empty object
  return {};
};

/**
 * Converts new mappingConfig format to old columnMappings format for backward compatibility
 * @param {Object} mappingConfig - The new mapping configuration
 * @returns {Object} Column mappings in the old format
 */
export const convertMappingConfigToColumnMappings = (mappingConfig) => {
  if (!mappingConfig || !mappingConfig.columns) {
    return {};
  }

  const columnMappings = {};

  // Convert each column from new format to old format
  Object.entries(mappingConfig.columns).forEach(([fieldName, columnConfig]) => {
    if (columnConfig.enabled && columnConfig.mapping) {
      const {
        csvName, type, cellType, unit,
      } = columnConfig.mapping;

      // Create mapping in old format
      columnMappings[fieldName] = {
        csvName,
        type: cellType || type, // Use cellType if available, fallback to type
        ...(unit && { unit }), // Only add unit if it exists
      };
    }
  });

  return columnMappings;
};

/**
 * Checks if an inventory uses the new mapping configuration format
 * @param {Object} inventory - The inventory object
 * @returns {boolean} True if using new format
 */
export const isNewMappingFormat = (inventory) => !!inventory.mappingConfig;

/**
 * Gets the mapping configuration in the new format, converting from legacy if needed
 * @param {Object} inventory - The inventory object
 * @returns {Object} Mapping configuration in new format
 */
export const getMappingConfig = (inventory) => {
  // If already in new format, return it
  if (inventory.mappingConfig) {
    return inventory.mappingConfig;
  }

  // If in legacy format, we need to convert it
  // This is a simplified conversion - in practice, you might want to use the full conversion logic
  if (inventory.columnMappings) {
    return {
      name: 'legacy',
      columnDelimiter: ';',
      decimalSeparator: ',',
      startFromLine: 1,
      timestamp: new Date(),
      columns: convertLegacyToNewFormat(inventory.columnMappings),
      customFields: inventory.customOperatorFields || [],
    };
  }

  return null;
};

/**
 * Converts legacy columnMappings to new format columns structure
 * @param {Object} columnMappings - Legacy column mappings
 * @returns {Object} Columns in new format
 */
const convertLegacyToNewFormat = (columnMappings) => {
  const columns = {};

  // console.log('columnMappings', columnMappings);

  Object.entries(columnMappings).forEach(([fieldName, mapping]) => {
    columns[fieldName] = {
      enabled: true,
      mapping: {
        csvName: mapping.csvName,
        type: mapping.type,
        cellType: mapping.type, // Assume cellType is same as type in legacy
        ...(mapping.unit && { unit: mapping.unit }),
      },
      displayStyle: 'editableText', // Default display style
      autoFill: {
        enabled: false,
        value: '',
      },
    };
  });

  // console.log('columns', columns);

  return columns;
};

/**
 * Gets custom operator fields from either old or new mapping format
 * @param {Object} inventory - The inventory object
 * @returns {Array} Custom operator fields array
 */
export const getCustomOperatorFields = (inventory) => {
  // New format
  if (inventory.mappingConfig) {
    return inventory.mappingConfig.customFields || [];
  }

  // Legacy format
  if (inventory.customOperatorFields) {
    return inventory.customOperatorFields;
  }

  // Fallback
  return [];
};

/**
 * Gets display styles from either old or new mapping format
 * @param {Object} inventory - The inventory object
 * @returns {Object} Display styles object
 */
export const getDisplayStyles = (inventory) => {
  // New format
  if (inventory.mappingConfig && inventory.mappingConfig.columns) {
    const displayStyles = {};
    Object.entries(inventory.mappingConfig.columns).forEach(([fieldName, columnConfig]) => {
      if (columnConfig.displayStyle) {
        displayStyles[fieldName] = columnConfig.displayStyle;
      }
    });
    return displayStyles;
  }

  // Legacy format
  if (inventory.displayStyles) {
    return inventory.displayStyles;
  }

  // Fallback
  return {};
};
